import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EDSButton } from '@gehc-eds-react/eds-button';
import { EDSTextField } from '@gehc-eds-react/eds-text-field';
import { apiFetch } from '../../utils/apiClient';
import { addErrorNotification, addSuccessNotification } from '../../redux/slices/notificationSlice';
import { useAppDispatch } from '../../redux/hooks';

const JOBS_API_BASE = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';

const readValue = (value) => {
    if (value === null || value === undefined || value === '') return '--';
    return value;
};
 
const getJobStatusStyles = (status) => {
    const normalizedStatus = (status || '').toString().trim().toLowerCase();
 
    if (normalizedStatus === 'complete' || normalizedStatus === 'success' || normalizedStatus === 'succeeded') {
        return {
            backgroundColor: '#e8f5e9',
            color: '#139c48',
            borderColor: '#c8e6c9'
        };
    }
 
    if (normalizedStatus === 'failed' || normalizedStatus === 'error') {
        return {
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderColor: '#ffcdd2'
        };
    }
 
    if (normalizedStatus === 'running' || normalizedStatus === 'in progress' || normalizedStatus === 'processing') {
        return {
            backgroundColor: '#e3f2fd',
            color: '#1565c0',
            borderColor: '#bbdefb'
        };
    }
 
    if (normalizedStatus === 'queued' || normalizedStatus === 'pending' || normalizedStatus === 'submitted') {
        return {
            backgroundColor: '#fff8e1',
            color: '#8d6e63',
            borderColor: '#ffe082'
        };
    }
 
    return {
        backgroundColor: '#f3f4f6',
        color: '#4b5563',
        borderColor: '#e5e7eb'
    };
};
 
const SourceDetailsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { sourceId } = useParams();
 
    const source = location.state?.source || null;
    const selectedContract = location.state?.selectedContract || null;
    const [jobList, setJobList] = useState([]);
    const [isJobsLoading, setIsJobsLoading] = useState(false);
    const [jobsError, setJobsError] = useState('');
 
    const sourceQueryId = useMemo(
        () => source?.source_id || source?.sourceId || sourceId || '',
        [source, sourceId]
    );

    const loadJobs = useCallback(async () => {
        if (!sourceQueryId) {
            setJobList([]);
            setJobsError('Source id is missing for this source.');
            return;
        }

        setIsJobsLoading(true);
        setJobsError('');

        try {
            const query = new URLSearchParams({
                source_id: sourceQueryId,
                limit: '50'
            });

            const response = await apiFetch(`${JOBS_API_BASE}/api/v1/s3-batch/jobs?${query.toString()}`, {
                method: 'GET',
                retry: false
            });

            setJobList(Array.isArray(response?.jobs) ? response.jobs : []);
        } catch (error) {
            setJobList([]);
            setJobsError(error.message || 'Failed to load jobs for this source.');
        } finally {
            setIsJobsLoading(false);
        }
    }, [sourceQueryId]);

    useEffect(() => {
        loadJobs();
    }, [loadJobs]);
    const dispatch = useAppDispatch();
 
    // ----- Create Job modal state -----
    const [isCreateJobPopupOpen, setIsCreateJobPopupOpen] = useState(false);
    const [isCreatingJob, setIsCreatingJob] = useState(false);
 
    // 'filter' -> replicate_prefix / include_completed / created_after
    // 'prefix' -> prefixes[]
    const [jobMode, setJobMode] = useState('filter');
 
    const [jobAccountId, setJobAccountId] = useState('');
    const [jobReplicatePrefix, setJobReplicatePrefix] = useState('');
    const [jobKeySuffix, setJobKeySuffix] = useState('');
    const [jobIncludeCompleted, setJobIncludeCompleted] = useState(false);
    const [jobCreatedAfter, setJobCreatedAfter] = useState('');
    const [jobPrefixesText, setJobPrefixesText] = useState('');
    const [jobDatasetName, setJobDatasetName] = useState('');
    const [jobDescription, setJobDescription] = useState('');
    const [jobReplicatePrefixError, setJobReplicatePrefixError] = useState('');
    const [jobPrefixesError, setJobPrefixesError] = useState('');
    const [jobDatasetNameError, setJobDatasetNameError] = useState('');
 
    const resolvedSourceId = source?.source_id || source?.sourceId || sourceId;
    const resolvedContractId = selectedContract?.ContractID || selectedContract?.contract_id || source?.contract_id || '';
    const hasRunTestConnection = Boolean(source?.connection_status || source?.last_tested_at);
 
    const goBack = () => {
        navigate('/s3-upload', { state: { selectedContract } });
    };
 
    const openCreateJobPopup = () => {
        // Pre-fill from the current source so the modal reflects what it's creating a job for.
        setJobMode('filter');
        setJobAccountId(source?.account_id || '');
        setJobReplicatePrefix(source?.replicate_prefix || '');
        setJobKeySuffix('');
        setJobIncludeCompleted(false);
        setJobCreatedAfter('');
        setJobPrefixesText('');
        setJobDatasetName('');
        setJobDescription('');
        setJobReplicatePrefixError('');
        setJobPrefixesError('');
        setJobDatasetNameError('');
        setIsCreateJobPopupOpen(true);
    };
 
    const handleCreateJobButtonClick = () => {
        if (!hasRunTestConnection) {
            dispatch(addErrorNotification({
                message: 'Run test connection for this source before creating a job.'
            }));
            return;
        }
        openCreateJobPopup();
    };
 
    const closeCreateJobPopup = () => {
        if (isCreatingJob) return;
        setIsCreateJobPopupOpen(false);
    };
 
    const saveCreateJob = async () => {
        // Prefix / Prefixes is mandatory, but which field depends on the selected mode.
        const parsedPrefixes = jobPrefixesText
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
 
        if (jobMode === 'prefix') {
            if (parsedPrefixes.length === 0) {
                setJobPrefixesError('Enter at least one prefix.');
                return;
            }
            setJobPrefixesError('');
        } else {
            if (!jobReplicatePrefix.trim()) {
                setJobReplicatePrefixError('Prefix is required.');
                return;
            }
            setJobReplicatePrefixError('');
        }

        if (!jobDatasetName.trim()) {
            setJobDatasetNameError('Dataset name is required.');
            return;
        }
        setJobDatasetNameError('');
 
        // Filter and Prefix modes submit different payload shapes to the create-job API.
        const payload = jobMode === 'prefix'
            ? {
                source_id: resolvedSourceId,
                contract_id: resolvedContractId,
                dataset_name: jobDatasetName.trim(),
                created_after: jobCreatedAfter ? new Date(jobCreatedAfter).toISOString() : null,
                description: jobDescription,
                prefixes: parsedPrefixes
            }
            : {
                source_id: resolvedSourceId,
                contract_id: resolvedContractId,
                dataset_name: jobDatasetName.trim(),
                replicate_prefix: jobReplicatePrefix,
                key_suffix: jobKeySuffix,
                include_completed: jobIncludeCompleted,
                // datetime-local gives "2026-07-06T15:00", convert to ISO with seconds/Z
                created_after: jobCreatedAfter ? new Date(jobCreatedAfter).toISOString() : null,
                description: jobDescription
            };
 
        setIsCreatingJob(true);
        try {
            await apiFetch(`${JOBS_API_BASE}/api/v1/s3-batch/jobs`, {
                method: 'POST',
                body: JSON.stringify(payload),
                contentType: 'application/json',
                retry: false
            });
 
            dispatch(addSuccessNotification({
                message: `Job created successfully for source "${resolvedSourceId}".`
            }));
 
            setIsCreateJobPopupOpen(false);
            await loadJobs();
        } catch (error) {
            dispatch(addErrorNotification({
                message: 'Failed to create job. Please try again.'
            }));
            console.error('Failed to create job:', error);
        } finally {
            setIsCreatingJob(false);
        }
    };
 
    return (
        <div className="data-upload-container">
            <div className="upload-data-header fill--1">
                <h1 className='u-mt0 u-mb0'>Source Details</h1>
            </div>
 
            <div className="flex user-groups-container">
                <div className="user-details-section u--1/4 fill--1" style={{ paddingBottom: '24px' }}>
                    <div>
                        <h4 className='u-mt0 u-mb--'>Source</h4>
                    </div>
 
                    <div className='u-mt'>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Source ID</div>
                        <div>{readValue(resolvedSourceId)}</div>
                    </div>
 
                    <div className='u-mt'>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Account Number</div>
                        <div>{readValue(source?.account_id)}</div>
                    </div>
 
                    <div className='u-mt'>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Bucket Name</div>
                        <div>{readValue(source?.source_bucket)}</div>
                    </div>
 
                    <div className='u-mt'>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Prefix</div>
                        <div>{readValue(source?.replicate_prefix)}</div>
                    </div>
 
                    <div className='u-mt'>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>Display Name</div>
                        <div>{readValue(source?.display_name)}</div>
                    </div>
                </div>
 
                <div
                    className="u--3/4 fill--1"
                    style={{
                        padding: '12px 24px 0 24px',
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 0
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <h4 className='u-mt0 u-mb--'>Job List</h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <EDSButton
                                variant="primary"
                                type="button"
                                label="Create Job"
                                onEDSButtonClick={handleCreateJobButtonClick}
                                onClick={handleCreateJobButtonClick}
                            />
                            <EDSButton
                                variant="secondary"
                                type="button"
                                label="Back"
                                onEDSButtonClick={goBack}
                                onClick={goBack}
                            />
                        </div>
                    </div>
 
                    <div
                        style={{
                            marginTop: '12px',
                            flex: '1 1 auto',
                            minHeight: 0,
                            overflow: 'auto'
                        }}
                    >
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                            <thead>
                                <tr>
                                    <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '10px 8px' }}>Job ID</th>
                                    <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '10px 8px' }}>Status</th>
                                    <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '10px 8px' }}>Submitted At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isJobsLoading && (
                                    <tr>
                                        <td colSpan={3} style={{ padding: '12px 8px', color: '#666' }}>
                                            Loading jobs...
                                        </td>
                                    </tr>
                                )}
 
                                {!isJobsLoading && jobsError && (
                                    <tr>
                                        <td colSpan={3} style={{ padding: '12px 8px', color: '#d32f2f' }}>
                                            {jobsError}
                                        </td>
                                    </tr>
                                )}
 
                                {!isJobsLoading && !jobsError && jobList.length === 0 && (
                                    <tr>
                                        <td colSpan={3} style={{ padding: '12px 8px', color: '#666' }}>
                                            No jobs found for this source.
                                        </td>
                                    </tr>
                                )}
 
                                {jobList.map((job, index) => (
                                    <tr key={`${job.job_id || job.id || 'job'}-${index}`}>
 
                                        <td
                                            style={{
                                                borderBottom: '1px solid #f1f1f1',
                                                padding: '10px 8px',
                                                color: '#0078d4',
                                                cursor: 'pointer'
                                            }}
                                            onClick={() => navigate(`/job-details/${job.job_id || job.id}`)}
                                        >
                                            {readValue(job.job_id || job.id)}
                                        </td>
 
                                        <td style={{ borderBottom: '1px solid #f1f1f1', padding: '10px 8px' }}>
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    padding: '4px 10px',
                                                    borderRadius: '999px',
                                                    border: '1px solid',
                                                    fontSize: '12px',
                                                    fontWeight: 700,
                                                    lineHeight: 1,
                                                    ...getJobStatusStyles(job.status)
                                                }}
                                            >
                                                {readValue(job.status)}
                                            </span>
                                        </td>
                                        <td style={{ borderBottom: '1px solid #f1f1f1', padding: '10px 8px' }}>
                                            {readValue(job.submitted_at || job.started_at || job.created_at)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {isCreateJobPopupOpen && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(17, 24, 39, 0.45)',
                        backdropFilter: 'blur(2px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2100,
                        padding: '24px'
                    }}
                    onClick={closeCreateJobPopup}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="create-job-title"
                        onClick={(event) => event.stopPropagation()}
                        style={{
                            width: '100%',
                            maxWidth: '660px',
                            backgroundColor: '#fff',
                            borderRadius: '14px',
                            boxShadow: '0 24px 60px rgba(17, 24, 39, 0.28)',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: '16px',
                                padding: '28px 32px 20px 32px',
                                borderBottom: '1px solid #edeff2'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                                <div
                                    style={{
                                        width: '44px',
                                        height: '44px',
                                        borderRadius: '10px',
                                        background: 'linear-gradient(135deg, #6D55B6 0%, #6C54B4 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0
                                    }}
                                >
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                        <path d="M12 5v14M5 12h14" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 id="create-job-title" style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>
                                        Create Job
                                    </h3>
                                    <p style={{ margin: '4px 0 0 0', fontSize: '13.5px', color: '#6b7280' }}>
                                        Start a new replication job for this source
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={closeCreateJobPopup}
                                aria-label="Close"
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    padding: '6px',
                                    borderRadius: '6px',
                                    color: '#9ca3af',
                                    lineHeight: 0
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            </button>
                        </div>
 
                        {/* Body */}
                        <div style={{ padding: '28px 32px', maxHeight: '65vh', overflowY: 'auto' }}>
                            <div
                                style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: '#9ca3af',
                                    marginBottom: '14px'
                                }}
                            >
                                Source
                            </div>
 
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '18px', marginBottom: '8px' }}>
                                <EDSTextField
                                    name="create-job-source-id"
                                    label={{ width: '', position: 'top', text: 'Source ID' }}
                                    value={readValue(resolvedSourceId)}
                                    width="100%"
                                    type="text"
                                    isDisabled={true}
                                />
                            </div>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '18px',
                                    marginTop: '18px'
                                }}
                            >
                                <EDSTextField
                                    name="create-job-account-id"
                                    label={{ width: '', position: 'top', text: 'Account Number' }}
                                    value={jobAccountId}
                                    placeholder="Enter account number"
                                    isDisabled={true}
                                    width="100%"
                                    type="text"
                                    maxLength={12}
                                />
                            </div>
 
                            {/* Filter / Prefix mode selector */}
                            <div
                                style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: '#9ca3af',
                                    margin: '28px 0 14px 0'
                                }}
                            >
                                Job Type
                            </div>
                            <div
                                role="tablist"
                                aria-label="Job creation mode"
                                style={{
                                    display: 'inline-flex',
                                    border: '1.5px solid #dde1e7',
                                    borderRadius: '999px',
                                    padding: '3px',
                                    gap: '2px'
                                }}
                            >
                                {[
                                    { key: 'filter', label: 'Filter' },
                                    { key: 'prefix', label: 'Prefix' }
                                ].map((option) => (
                                    <button
                                        key={option.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={jobMode === option.key}
                                        onClick={() => {
                                            setJobMode(option.key);
                                            setJobReplicatePrefixError('');
                                            setJobPrefixesError('');
                                            setJobDatasetNameError('');
                                        }}
                                        style={{
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '7px 20px',
                                            borderRadius: '999px',
                                            fontSize: '13px',
                                            fontWeight: 700,
                                            backgroundColor: jobMode === option.key ? '#6D55B6' : 'transparent',
                                            color: jobMode === option.key ? '#fff' : '#4b5563',
                                            transition: 'background-color 0.15s ease, color 0.15s ease'
                                        }}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>

                            <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280', lineHeight: 1.45 }}>
                                <div><strong>Filter:</strong> Bulk under one prefix, any scale</div>
                                <div><strong>Prefixes:</strong> Multiple non-adjacent folders in one job</div>
                            </div>
 
                            {jobMode === 'filter' ? (
                                <>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: '18px',
                                            marginTop: '18px'
                                        }}
                                    >
                                        <div>
                                            <EDSTextField
                                                name="create-job-replicate-prefix"
                                                label={{ width: '', position: 'top', text: 'Prefix' }}
                                                value={jobReplicatePrefix}
                                                placeholder="Enter prefix"
                                                isRequired={true}
                                                validationError={jobReplicatePrefixError}
                                                width="100%"
                                                type="text"
                                                onEDSTextFieldChange={(e) => {
                                                    setJobReplicatePrefix(e.target.value);
                                                    if (jobReplicatePrefixError) setJobReplicatePrefixError('');
                                                }}
                                            />
                                            {jobReplicatePrefixError && (
                                                <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                                                    {jobReplicatePrefixError}
                                                </div>
                                            )}
                                        </div>

                                        <EDSTextField
                                            name="create-job-key-suffix"
                                            label={{ width: '', position: 'top', text: 'Key Suffix' }}
                                            value={jobKeySuffix}
                                            placeholder=".zip"
                                            isRequired={false}
                                            width="100%"
                                            type="text"
                                            onEDSTextFieldChange={(e) => setJobKeySuffix(e.target.value)}
                                        />
                                    </div>
 
                                    <div
                                        style={{
                                            fontSize: '11px',
                                            fontWeight: 700,
                                            letterSpacing: '0.06em',
                                            textTransform: 'uppercase',
                                            color: '#9ca3af',
                                            margin: '28px 0 14px 0'
                                        }}
                                    >
                                        Job Options
                                    </div>
 
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: '18px',
                                            alignItems: 'end'
                                        }}
                                    >
                                        <div>
                                            <label
                                                htmlFor="create-job-created-after"
                                                style={{
                                                    display: 'block',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    color: '#111827',
                                                    marginBottom: '8px'
                                                }}
                                            >
                                                Created After
                                            </label>
                                            <div style={{ position: 'relative' }}>
                                                <svg
                                                    width="16"
                                                    height="16"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    style={{
                                                        position: 'absolute',
                                                        left: '12px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        pointerEvents: 'none'
                                                    }}
                                                >
                                                    <rect x="3" y="5" width="18" height="16" rx="2.5" stroke="#8a94a6" strokeWidth="1.6" />
                                                    <path d="M3 9.5h18" stroke="#8a94a6" strokeWidth="1.6" />
                                                    <path d="M8 3v3.4M16 3v3.4" stroke="#8a94a6" strokeWidth="1.6" strokeLinecap="round" />
                                                </svg>
                                                <input
                                                    id="create-job-created-after"
                                                    type="datetime-local"
                                                    name="create-job-created-after"
                                                    value={jobCreatedAfter}
                                                    onChange={(e) => setJobCreatedAfter(e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '10px 12px 10px 36px',
                                                        border: '1.5px solid #dde1e7',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        color: '#111827',
                                                        boxSizing: 'border-box',
                                                        outline: 'none',
                                                        transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                                        fontFamily: 'inherit'
                                                    }}
                                                    onFocus={(e) => {
                                                        e.target.style.borderColor = '#0091DA';
                                                        e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                                    }}
                                                    onBlur={(e) => {
                                                        e.target.style.borderColor = '#dde1e7';
                                                        e.target.style.boxShadow = 'none';
                                                    }}
                                                />
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                                                Only include objects created after this date and time.
                                            </div>
                                        </div>
 
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                border: '1.5px solid #dde1e7',
                                                borderRadius: '8px',
                                                padding: '12px 14px',
                                                height: '44px',
                                                marginBottom: '28px',
                                                boxSizing: 'border-box'
                                            }}
                                        >
                                            <span style={{ fontSize: '13.5px', fontWeight: 600, color: '#111827' }}>
                                                Include Completed
                                            </span>
                                            <button
                                                type="button"
                                                role="switch"
                                                aria-checked={jobIncludeCompleted}
                                                onClick={() => setJobIncludeCompleted((prev) => !prev)}
                                                style={{
                                                    position: 'relative',
                                                    width: '40px',
                                                    height: '22px',
                                                    borderRadius: '999px',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: 0,
                                                    backgroundColor: jobIncludeCompleted ? '#0091DA' : '#d1d5db',
                                                    transition: 'background-color 0.15s ease',
                                                    flexShrink: 0
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        position: 'absolute',
                                                        top: '2px',
                                                        left: jobIncludeCompleted ? '20px' : '2px',
                                                        width: '18px',
                                                        height: '18px',
                                                        borderRadius: '50%',
                                                        backgroundColor: '#fff',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                                                        transition: 'left 0.15s ease'
                                                    }}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div style={{ marginTop: '18px' }}>
                                    <label
                                        htmlFor="create-job-prefixes"
                                        style={{
                                            display: 'block',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: '#111827',
                                            marginBottom: '8px'
                                        }}
                                    >
                                        Prefixes <span style={{ color: '#d32f2f' }}>*</span>
                                    </label>
                                    <textarea
                                        id="create-job-prefixes"
                                        value={jobPrefixesText}
                                        onChange={(e) => {
                                            setJobPrefixesText(e.target.value);
                                            if (jobPrefixesError) setJobPrefixesError('');
                                        }}
                                        placeholder={'batches/2026-01-a/\nbatches/2026-01-b/'}
                                        rows={4}
                                        style={{
                                            width: '100%',
                                            padding: '10px 12px',
                                            border: jobPrefixesError ? '1.5px solid #d32f2f' : '1.5px solid #dde1e7',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            color: '#111827',
                                            boxSizing: 'border-box',
                                            outline: 'none',
                                            fontFamily: 'inherit',
                                            resize: 'vertical'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#0091DA';
                                            e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = jobPrefixesError ? '#d32f2f' : '#dde1e7';
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    />
                                    {jobPrefixesError ? (
                                        <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '6px' }}>
                                            {jobPrefixesError}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                                            One prefix per line, e.g. batches/2026-01-a/
                                        </div>
                                    )}

                                    <div style={{ marginTop: '16px' }}>
                                        <label
                                            htmlFor="create-job-prefix-created-after"
                                            style={{
                                                display: 'block',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                color: '#111827',
                                                marginBottom: '8px'
                                            }}
                                        >
                                            Created After
                                        </label>
                                        <input
                                            id="create-job-prefix-created-after"
                                            type="datetime-local"
                                            name="create-job-prefix-created-after"
                                            value={jobCreatedAfter}
                                            onChange={(e) => setJobCreatedAfter(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '1.5px solid #dde1e7',
                                                borderRadius: '8px',
                                                fontSize: '14px',
                                                color: '#111827',
                                                boxSizing: 'border-box',
                                                outline: 'none',
                                                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                                                fontFamily: 'inherit'
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = '#0091DA';
                                                e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = '#dde1e7';
                                                e.target.style.boxShadow = 'none';
                                            }}
                                        />
                                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '6px' }}>
                                            Only include objects created after this date and time.
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Dataset Name / Description — shared by both Filter and Prefix modes */}
                            <div style={{ marginTop: '18px' }}>
                                <EDSTextField
                                    name="create-job-dataset-name"
                                    label={{ width: '', position: 'top', text: 'Dataset Name' }}
                                    value={jobDatasetName}
                                    placeholder="Enter dataset name"
                                    isRequired={true}
                                    validationError={jobDatasetNameError}
                                    width="100%"
                                    type="text"
                                    onEDSTextFieldChange={(e) => {
                                        setJobDatasetName(e.target.value);
                                        if (jobDatasetNameError) setJobDatasetNameError('');
                                    }}
                                />
                                {jobDatasetNameError && (
                                    <div style={{ color: '#d32f2f', fontSize: '12px', marginTop: '4px' }}>
                                        {jobDatasetNameError}
                                    </div>
                                )}
                            </div>
                            <div style={{ marginTop: '18px' }}>
                                <label
                                    htmlFor="create-job-description"
                                    style={{
                                        display: 'block',
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: '#111827',
                                        marginBottom: '8px'
                                    }}
                                >
                                    Description
                                </label>
                                <textarea
                                    id="create-job-description"
                                    value={jobDescription}
                                    onChange={(e) => setJobDescription(e.target.value)}
                                    placeholder="Describe what this job is for"
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px',
                                        border: '1.5px solid #dde1e7',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        color: '#111827',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        fontFamily: 'inherit',
                                        resize: 'vertical'
                                    }}
                                    onFocus={(e) => {
                                        e.target.style.borderColor = '#0091DA';
                                        e.target.style.boxShadow = '0 0 0 3px rgba(0, 145, 218, 0.14)';
                                    }}
                                    onBlur={(e) => {
                                        e.target.style.borderColor = '#dde1e7';
                                        e.target.style.boxShadow = 'none';
                                    }}
                                />
                            </div>
                        </div>
 
                        {/* Footer */}
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '10px',
                                padding: '18px 32px',
                                backgroundColor: '#fafbfc',
                                borderTop: '1px solid #edeff2'
                            }}
                        >
                            <EDSButton
                                variant="secondary"
                                type="button"
                                label="Cancel"
                                onEDSButtonClick={closeCreateJobPopup}
                                isDisabled={isCreatingJob}
                            />
                            <EDSButton
                                variant="primary"
                                type="button"
                                label={isCreatingJob ? 'Creating...' : 'Create Job'}
                                onEDSButtonClick={saveCreateJob}
                                isDisabled={isCreatingJob}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
 
export default SourceDetailsPage;
 
 